import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  DollarSign,
  Users,
  Building2,
  MessageSquare,
  ThumbsUp,
  Zap,
} from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  getAICostsSummary,
  getAICostsByAccount,
  getAICostsByUser,
  getAICostsAccounts,
  getAIAnalyticsSummary,
  getAIAnalyticsConversations,
} from "@/lib/api";

// --- Constants ---

const CONTEXT_TYPES = [
  { value: "all", label: "Vse" },
  { value: "menu_import", label: "Menu Import" },
  { value: "layout_settings", label: "Layout Settings" },
  { value: "content_edit", label: "Content Edit" },
];

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
];

const TOPIC_LABELS: Record<string, string> = {
  fonts: "Fonty",
  spacing: "Mezery",
  layout: "Layout",
  printing: "Tisk",
  visual_issue: "Vizualni problem",
  icons: "Ikony",
  other: "Ostatni",
};

// --- Overview Tab (former AICostsPage) ---

function OverviewTab() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [accountFilter, setAccountFilter] = useState<string>("all");
  const [contextType, setContextType] = useState<string>("all");

  // Fetch accounts for filter dropdown
  const { data: accounts = [] } = useQuery({
    queryKey: ["ai-costs-accounts"],
    queryFn: getAICostsAccounts,
  });

  // Build filter params
  const filterParams = {
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    account_id: accountFilter !== "all" ? accountFilter : undefined,
    context_type: contextType !== "all" ? contextType : undefined,
  };

  // Fetch summary
  const { data: summary } = useQuery({
    queryKey: ["ai-costs-summary", filterParams],
    queryFn: () => getAICostsSummary(filterParams),
  });

  // Fetch by-account data
  const { data: byAccount = [], isLoading: byAccountLoading } = useQuery({
    queryKey: ["ai-costs-by-account", filterParams],
    queryFn: () => getAICostsByAccount(filterParams),
  });

  // Fetch by-user data
  const { data: byUser = [], isLoading: byUserLoading } = useQuery({
    queryKey: ["ai-costs-by-user", filterParams],
    queryFn: () => getAICostsByUser(filterParams),
  });

  const formatCurrency = (value: string | number) =>
    `$${Number(value).toFixed(2)}`;

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setAccountFilter("all");
    setContextType("all");
  };

  const hasFilters =
    dateFrom || dateTo || accountFilter !== "all" || contextType !== "all";

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filtry</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-[160px]"
                placeholder="Od"
              />
              <span className="text-muted-foreground">-</span>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-[160px]"
                placeholder="Do"
              />
            </div>

            <Select value={contextType} onValueChange={setContextType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Context Type" />
              </SelectTrigger>
              <SelectContent>
                {CONTEXT_TYPES.map((ct) => (
                  <SelectItem key={ct.value} value={ct.value}>
                    {ct.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={accountFilter} onValueChange={setAccountFilter}>
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Account" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Vsechny ucty</SelectItem>
                {accounts.map((account) => (
                  <SelectItem
                    key={account.account_id}
                    value={account.account_id}
                  >
                    {account.account_name} ({account.conversation_count})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Zrusit filtry
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Celkove naklady
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary ? formatCurrency(summary.total_cost_usd) : "-"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Konverzaci</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary?.total_conversations.toLocaleString() ?? "-"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Uctu</CardTitle>
            <Building2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary?.total_accounts ?? "-"}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Prumer / konverzace
            </CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {summary && summary.total_conversations > 0
                ? formatCurrency(
                    Number(summary.total_cost_usd) /
                      summary.total_conversations,
                  )
                : "-"}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs with Tables */}
      <Tabs defaultValue="by-account" className="space-y-4">
        <TabsList>
          <TabsTrigger value="by-account">Po uctech</TabsTrigger>
          <TabsTrigger value="by-user">Po uzivatelich</TabsTrigger>
        </TabsList>

        <TabsContent value="by-account">
          <Card>
            <CardContent className="p-0">
              {byAccountLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-muted-foreground">Nacitam...</div>
                </div>
              ) : byAccount.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-muted-foreground">Zadna data</div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead className="text-right">Konverzaci</TableHead>
                      <TableHead className="text-right">
                        Naklady (USD)
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byAccount.map((item) => (
                      <TableRow key={item.account_id}>
                        <TableCell className="font-medium">
                          {item.account_name}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.conversations.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(item.cost_usd)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="by-user">
          <Card>
            <CardContent className="p-0">
              {byUserLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-muted-foreground">Nacitam...</div>
                </div>
              ) : byUser.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-muted-foreground">Zadna data</div>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Uzivatel</TableHead>
                      <TableHead>Account</TableHead>
                      <TableHead className="text-right">Konverzaci</TableHead>
                      <TableHead className="text-right">
                        Naklady (USD)
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {byUser.map((item) => (
                      <TableRow key={`${item.user_id}-${item.account_name}`}>
                        <TableCell className="font-medium">
                          {item.user_email}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {item.account_name}
                        </TableCell>
                        <TableCell className="text-right">
                          {item.conversations.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(item.cost_usd)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// --- Analytics Tab (former AIAnalyticsPage) ---

function AnalyticsTab() {
  const [days, setDays] = useState(30);
  const [topicFilter, setTopicFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const navigate = useNavigate();

  const { data: summary, isLoading: summaryLoading } = useQuery({
    queryKey: ["ai-analytics-summary", days],
    queryFn: () => getAIAnalyticsSummary(days),
  });

  const {
    data: conversations,
    isLoading: conversationsLoading,
    error: conversationsError,
  } = useQuery({
    queryKey: ["ai-analytics-conversations", days, topicFilter, page],
    queryFn: () =>
      getAIAnalyticsConversations({
        days,
        topic: topicFilter === "all" ? undefined : topicFilter,
        page,
        page_size: pageSize,
      }),
  });

  const totalPages = conversations
    ? Math.max(1, Math.ceil(conversations.total / pageSize))
    : 1;

  if (summaryLoading) {
    return <div className="p-8">Nacitam...</div>;
  }

  const overview = summary?.overview;

  return (
    <div className="space-y-8">
      {/* Period Selector */}
      <div className="flex justify-end">
        <Select
          value={days.toString()}
          onValueChange={(v) => {
            setDays(parseInt(v));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Poslednich 7 dni</SelectItem>
            <SelectItem value="30">Poslednich 30 dni</SelectItem>
            <SelectItem value="90">Poslednich 90 dni</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Konverzace</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overview?.total_conversations || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Prumer {overview?.avg_turns_per_conversation.toFixed(1)} zprav
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Acceptance Rate
            </CardTitle>
            <ThumbsUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {((overview?.acceptance_rate || 0) * 100).toFixed(0)}%
            </div>
            <p className="text-xs text-muted-foreground">
              {overview?.suggestions_accepted || 0} /{" "}
              {overview?.total_suggestions || 0} navrhu
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Naklady</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${overview?.total_cost_usd.toFixed(2) || "0.00"}
            </div>
            <p className="text-xs text-muted-foreground">
              {((overview?.total_tokens_in || 0) / 1000).toFixed(0)}k in /{" "}
              {((overview?.total_tokens_out || 0) / 1000).toFixed(0)}k out
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">
              Se screenshotem
            </CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {overview?.conversations_with_screenshots || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              {overview?.total_conversations
                ? (
                    (overview.conversations_with_screenshots /
                      overview.total_conversations) *
                    100
                  ).toFixed(0)
                : 0}
              % konverzaci
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Topics Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Temata konverzaci</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={summary?.topics.map((t) => ({
                      name: TOPIC_LABELS[t.topic] || t.topic,
                      value: t.count,
                    }))}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) =>
                      `${name} ${((percent ?? 0) * 100).toFixed(0)}%`
                    }
                  >
                    {summary?.topics.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Problematic Settings Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Problematicka nastaveni</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {summary?.problematic_settings.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={summary.problematic_settings.slice(0, 5).map((s) => ({
                      name: s.setting_path.split(".").pop(),
                      rejection: Math.round(s.rejection_rate * 100),
                      total: s.total_suggestions,
                    }))}
                    layout="vertical"
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} unit="%" />
                    <YAxis type="category" dataKey="name" width={100} />
                    <Tooltip
                      formatter={(value, name) =>
                        name === "rejection" ? `${value}%` : value
                      }
                    />
                    <Bar
                      dataKey="rejection"
                      fill="#ef4444"
                      name="Rejection rate"
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  Zadna data
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Conversations */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Posledni konverzace</CardTitle>
          <Select
            value={topicFilter}
            onValueChange={(v) => {
              setTopicFilter(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Vsechna temata" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Vsechna temata</SelectItem>
              {Object.entries(TOPIC_LABELS).map(([key, label]) => (
                <SelectItem key={key} value={key}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {conversationsError ? (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
              Konverzace se nepodařilo načíst: {conversationsError.message}
            </div>
          ) : conversationsLoading ? (
            <div>Načítám…</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Datum</TableHead>
                  <TableHead>Účet</TableHead>
                  <TableHead>Zprava</TableHead>
                  <TableHead>Tema</TableHead>
                  <TableHead>Navrhy</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {conversations?.items.map((conv) => (
                  <TableRow key={conv.id}>
                    <TableCell className="whitespace-nowrap">
                      {new Date(conv.created_at).toLocaleDateString("cs-CZ")}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{conv.account_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {conv.user_email}
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {conv.user_message || "-"}
                    </TableCell>
                    <TableCell>
                      {conv.topic_category && (
                        <Badge variant="outline">
                          {TOPIC_LABELS[conv.topic_category] ||
                            conv.topic_category}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-green-600">
                        {conv.suggestions_accepted}
                      </span>
                      {" / "}
                      <span className="text-red-600">
                        {conv.suggestions_rejected}
                      </span>
                      {" / "}
                      {conv.suggestions_count}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          navigate(`/menu-editor/ai-asistent/${conv.id}`)
                        }
                      >
                        Detail
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {conversations && conversations.total > pageSize && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-sm text-muted-foreground">
                {conversations.total} konverzací
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Předchozí
                </Button>
                <span className="text-sm text-muted-foreground">
                  {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Další
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// --- Main Page Component ---

export function AIAssistantPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">AI Asistent</h1>
        <p className="text-muted-foreground">
          Prehled nakladu a analyza konverzaci z Menu Editoru
        </p>
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Prehled</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab />
        </TabsContent>

        <TabsContent value="analytics">
          <AnalyticsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
